'use client';
import CountDownClock, {
  CountdownClockProps,
} from '@components/Navbar/SiteWideBanner/_CountdownClock';
import { isActivePromo } from '@components/Navigation/_SitewideBanner';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { MetaProps } from '@repo/ui/src/types/meta';
import { LinkProps } from '@repo/ui/src/types/link';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { promotionClicked } from '@repo/ui/src/services/analytics';
import { ImageProps } from '@repo/ui/src/types/image';

interface BannerLinkProps {
  link: LinkProps;
  onClick: () => void;
}

const BannerLink = ({ link, onClick }: BannerLinkProps) => {
  const linkObj = buildLinkObj({ link: link.link });
  const text = link.displayName;
  return linkObj && linkObj.href !== 'link-not-set-amp' ? (
    <span>
      <Anchor
        {...link}
        onClick={onClick}
        className="tw-underline tw-text-primary-100 hover:tw-text-action-300 hover:tw-underline"
      >
        {text}
      </Anchor>
    </span>
  ) : (
    <div>{text}</div>
  );
};

export interface Props {
  _meta: MetaProps;
  content: Array<{
    link: LinkProps;
    title: string;
    promotion_id: string;
    countdown: CountdownClockProps;
  }>;
  bannerLabel: string;
  staticNavLinks: Array<{ link: LinkProps; icon: ImageProps }>;
  rotationTime?: number;
}

const NavbarSitewideBanner = ({
  bannerLabel,
  content,
  staticNavLinks,
  rotationTime = 5000,
}: Props) => {
  const promotions = React.useMemo(
    () =>
      content?.filter((promo) =>
        isActivePromo({ link: promo.link, countdownClock: promo.countdown }),
      ),
    [content],
  );
  const rotator = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const time = rotationTime / 1000;
    const t1 = gsap.timeline({ repeat: -1 });

    if (content?.length === 1) return; // Don't rotate with only one message

    content.forEach((c, i) => {
      const target = `.slider-${i}`;
      if (i > 0) {
        gsap.set(target, { x: 100, opacity: 0, pointerEvents: 'none' });
        t1.to(target, { x: 0, opacity: 1, pointerEvents: 'all' }, '>');
      } else {
        t1.fromTo(target, { x: 100, opacity: 0 }, { x: 0, opacity: 1 }, '>');
      }
      t1.addLabel(`${target}`);
      t1.to(target, { x: -100, opacity: 0, pointerEvents: 'none', delay: time }, '>');
    });

    if (rotator.current) {
      rotator.current.addEventListener('mouseenter', () => {
        // Pause the rotation so someone can click it
        t1.pause(t1.currentLabel());
      });

      rotator.current.addEventListener('mouseleave', () => {
        t1.play();
      });
    }

    return () => {
      t1.kill();
    };
  }, []);

  const onPromotionClicked = useCallback((banner: any, index: number) => {
    promotionClicked({
      name: banner.displayName,
      creative: `top_banner_${index}`,
      position: 'sitewide_banner',
      promotion_id: banner.promotion_id,
    });
  }, []);

  return (
    <section
      className="tw-hidden tw-bg-surface-700 tw-py-2.5 tw-px-8 tw-z-[1000] tw-text-left tw-relative tw-items-center tw-w-full tw-max-w-screen md:tw-inline-block"
      aria-label={bannerLabel || 'Sitewide Banner for promotional and emergency messaging'}
      data-testid="site-wide-banner"
    >
      <div className="tw-grid tw-grid-cols-[1fr_auto]">
        <div className="tw-w-full tw-overflow-hidden tw-inline-block" ref={rotator}>
          <ul className="tw-m-0 tw-p-0 tw-relative tw-flex tw-flex-nowrap tw-w-max">
            {promotions?.map((item, i) => {
              const bannerTitle = item.title || null;

              return (
                <li
                  key={bannerTitle || `navbar-rotating-item${i}`}
                  className={classNames(
                    `slider-${i} tw-inline-block tw-justify-center tw-items-center tw-w-max tw-flex-wrap tw-text-white tra-text-web-body-m`,
                    i > 0 ? 'tw-absolute tw-opacity-0' : 'tw-relative tw-opacity-100',
                  )}
                >
                  <span className="tw-text-primary-100 tw-text-xs tw-leading-body tw-w-full">
                    {bannerTitle}&nbsp; |
                  </span>
                  {item.link.link && item.link.displayName && (
                    <span className="tw-text-primary-100 tw-text-xs tw-leading-body tw-w-full tw-pl-2">
                      <BannerLink link={item.link} onClick={() => onPromotionClicked(item, i)} />
                    </span>
                  )}
                  {item?.countdown && (
                    <>
                      &nbsp;
                      <CountDownClock {...item.countdown} />
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="tw-grid tw-grid-cols-[auto_auto] tw-gap-7 tw-items-center">
          {staticNavLinks?.map(({ link, icon }) => (
            <Anchor {...link} key={link.displayName} className="hover:!tw-text-action-300">
              <div className="tw-flex tw-gap-2 tw-w-max">
                {icon && <img src={buildImageSrc(icon)} alt="" />}
                <span className="tw-text-primary-100 tw-text-xs tw-leading-body tw-w-full tw-underline hover:!tw-text-action-300">
                  {link.displayName}
                </span>
              </div>
            </Anchor>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NavbarSitewideBanner;
